import { IInteractionRpc, IRpcs } from "@pulse-ui/codegen/generated_ts/managed_algo";
import { getDefaultRpcEntries, type DefaultSlot } from "./defaultRpcs";

export const CREATE_STRATEGY_INSTANCE_RPC = "create_strategy_instance";
export const STOPPED = "stopped";
export const STOPPING = "stopping";
export const RUNNING = "running";
export const STARTING = "starting";

// Disable logic is driven purely by the default slot and row status.
export function isRpcDisabledForStatus(
  slot: DefaultSlot | undefined,
  status: string | undefined
): boolean {
  if (!status || !slot) return false;
  const normalized = status.trim().toLowerCase();

  // Treat both "stopped" and "stopping" as non-runnable for stop/pause RPCs.
  if (normalized === STOPPED || normalized === STOPPING) {
    return slot === "stop" || slot === "pause";
  }

  // Treat both "running" and "starting" as non-runnable for resume RPCs.
  if (normalized === RUNNING || normalized === STARTING) {
    return slot === "resume";
  }

  return false;
}

export type FilterInteractionOptions =
  | { includeOnly: string[] }
  | { exclude: string[] };

function getRpcLabel(schema: unknown): string | undefined {
  if (!schema || typeof schema !== "object") return undefined;
  const label = (schema as Record<string, unknown>).label;
  return typeof label === "string" ? label : undefined;
}

export type InteractionDataByConnection = Record<string, IInteractionRpc>;

/**
 * Returns default RPCs for a connection (context menu quick actions: Pause, Kill, etc.).
 * Uses rpcs.default_rpcs only; each entry has actual rpcName and schema label/display_name.
 */
export function getDefaultRpcsForConnection(
  interactionData: InteractionDataByConnection | null | undefined,
  connectionId: string
): { slot: DefaultSlot; rpcName: string; displayName: string }[] {
  const conn = interactionData?.[connectionId];
  if (!conn) return [];
  const defaults = getDefaultRpcEntries(conn);
  return defaults
    // Context menu should never offer the "start" RPC (handled via Start Strategy modal)
    .filter(({ slot }) => slot !== "start")
    .map(({ slot, rpcName, schema }) => {
      const ui = (schema as Record<string, unknown>).ui as { display_name?: string } | undefined;
      const displayName = typeof ui?.display_name === "string" ? ui.display_name : rpcName;
      return { slot, rpcName, displayName };
    })
    .filter((x): x is { slot: DefaultSlot; rpcName: string; displayName: string } => x != null);
}

/**
 * Returns a new interaction object with each connection's `rpcs` filtered by each RPC's `label` field.
 * All connection keys are preserved; each value has only RPCs matching the filter.
 */
export function filterInteractionByRpcNames(
  interaction: Record<string, IInteractionRpc> | null | undefined,
  options: FilterInteractionOptions
): Record<string, IInteractionRpc> | null {
  if (!interaction) return null;

  const result: Record<string, IInteractionRpc> = {};

  for (const [connectionKey, interactionValue] of Object.entries(interaction)) {
    const rpcs = interactionValue?.rpcs as unknown as Record<string, any> | undefined;
    if (!rpcs || typeof rpcs !== "object") continue;

    const rpcEntries = Object.entries(rpcs);
    let filteredEntries: [string, unknown][];

    if ("includeOnly" in options) {
      const set = new Set(options.includeOnly);
      filteredEntries = rpcEntries.filter(([, schema]) => set.has(getRpcLabel(schema) ?? ""));
    } else {
      const set = new Set(options.exclude);
      filteredEntries = rpcEntries.filter(([, schema]) => !set.has(getRpcLabel(schema) ?? ""));
    }

    if (filteredEntries.length === 0) continue;

    const filteredRpcs = Object.fromEntries(filteredEntries);
    result[connectionKey] = {
      ...interactionValue,
      rpcs: filteredRpcs as unknown as IRpcs,
    };
  }

  if (Object.keys(result).length === 0) return null;
  return result;
}
